import React, { useState, useEffect, useRef, useCallback } from "react";
import { Maximize2 } from "lucide-react";
import { API_BASE } from "../../config/api";
import { getGameDetails } from "../../utils/gameDetailsCache";
import { isUserWinnerForGame } from "../../utils/chartUtils";
import { svgElementToPngFile } from "../../utils/chartSnapshot";
import { filterTimelineByRange } from "../../utils/timelineRangeFilter";
import logger from '../../utils/logger';

const BubbleChart = ({ timelineData, chartRange, rangeMode = 'sessions', onShare, shareStatus, onExpand, user, isFullscreen = false, shareTrigger = 0 }) => {
  const clipNs = React.useId().replace(/:/g, '');
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, game: null, sessions: 0, totalTime: 0 });
  const chartRef = useRef(null);
  const svgRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });
  const [bubbleData, setBubbleData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Mobile-aware responsive dimensions. Dimensions are only updated when they
  // actually change, and resizes are debounced so switching tabs / entering
  // fullscreen does not repeatedly recalculate the bubble layout.
  useEffect(() => {
    const computeSize = (width) => {
      let height;
      if (window.innerWidth <= 480) {
        // Mobile portrait: use more height to accommodate bubble layout
        height = Math.max(450, Math.min(600, window.innerHeight - 250));
      } else if (window.innerWidth <= 768) {
        // Tablet portrait: moderate height
        height = Math.max(400, Math.min(500, width * 0.8));
      } else {
        // Desktop: original calculation
        height = Math.max(300, Math.min(500, width * 0.6));
      }
      return { width, height };
    };

    const setIfChanged = (next) => {
      setDimensions((prev) => {
        if (prev.width === next.width && prev.height === next.height) {
          return prev;
        }
        return next;
      });
    };

    let resizeTimer;
    const updateDimensions = () => {
      const container = chartRef.current?.parentElement;
      if (!container) return;
      const width = container.clientWidth;
      if (width === 0) return;
      setIfChanged(computeSize(width));
    };

    const debouncedUpdate = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateDimensions, 150);
    };

    // Delay initial measurement to ensure DOM layout is settled
    const rafId = window.requestAnimationFrame(updateDimensions);

    window.addEventListener('resize', debouncedUpdate);

    // Use ResizeObserver to catch container size changes
    const container = chartRef.current?.parentElement;
    let resizeObserver;
    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(debouncedUpdate);
      resizeObserver.observe(container);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', debouncedUpdate);
      if (resizeTimer) clearTimeout(resizeTimer);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  // Calculate bubble data with playtime-based sizing
  const calculateBubbleData = useCallback(async () => {
    setLoading(true);
    const gameStats = new Map();
    const gameDetailsCache = new Map();

    // Filter timeline data based on the last N sessions or last N days
    const filteredData = filterTimelineByRange(timelineData, chartRange, rangeMode);

    // Collect unique game IDs
    const uniqueGameIds = [...new Set(filteredData.flatMap(dayEntry =>
      dayEntry.sessions ? dayEntry.sessions.map(s => s.game_id).filter(Boolean) : []
    ))];

    // Fetch game details for all unique games in parallel, reusing the shared
    // process-wide cache so tab switches / fullscreen / share do not re-request
    // the same details.
    await Promise.all(uniqueGameIds.map(async (gameId) => {
      const details = await getGameDetails(gameId);
      if (details) {
        gameDetailsCache.set(gameId, details);
      }
    }));

    // Aggregate game statistics with playtime-based totalTime
    filteredData.forEach(dayEntry => {
      if (dayEntry.sessions) {
        dayEntry.sessions.forEach(session => {
          const gameId = session.game_id;
          const gameName = session.game_name || 'Unknown Game';
          const gameDetails = gameDetailsCache.get(gameId);
          const gameImage = gameId ? `${API_BASE}/games/${gameId}/thumbnail` : null;

          if (!gameId) return;

          if (!gameStats.has(gameId)) {
            gameStats.set(gameId, {
              id: gameId,
              name: gameName,
              image: gameImage,
              sessions: 0,
              totalTime: 0
            });
          }

          const stats = gameStats.get(gameId);
          stats.sessions += 1;

          // Calculate playtime from game details
          let playtime = 60; // Default fallback
          if (gameDetails) {
            const minPlaytime = gameDetails.min_playtime;
            const maxPlaytime = gameDetails.max_playtime;
            const avgPlaytime = gameDetails.avg_playtime || gameDetails.playing_time;

            if (minPlaytime && maxPlaytime) {
              playtime = (minPlaytime + maxPlaytime) / 2;
            } else if (avgPlaytime) {
              playtime = avgPlaytime;
            }
          }
          stats.totalTime += playtime;
        });
      }
    });

    // Convert to array
    const bubbleArray = Array.from(gameStats.values()).map(stats => ({
      id: stats.id,
      gameName: stats.name,
      sessions: stats.sessions,
      totalTime: stats.totalTime,
      image: stats.image
    }));

    // Calculate bubble sizes and positions using collision-based layout
    const maxTime = Math.max(1, ...bubbleArray.map(b => b.totalTime));
    const minTime = Math.min(...bubbleArray.map(b => b.totalTime));
    const timeRange = maxTime - minTime || 1;

    // Mobile-aware bubble sizing
    let minRadius, maxRadius;
    if (window.innerWidth <= 480) {
      minRadius = 25;
      maxRadius = Math.min(80, Math.min(dimensions.width, dimensions.height) / 5);
    } else if (window.innerWidth <= 768) {
      minRadius = 30;
      maxRadius = Math.min(100, Math.min(dimensions.width, dimensions.height) / 4.5);
    } else {
      minRadius = 40;
      maxRadius = Math.min(120, Math.min(dimensions.width, dimensions.height) / 4);
    }

    // Edge margin keeps strokes and drop-shadows inside the SVG viewport.
    const edgeMargin = 10;

    const bubbles = bubbleArray.map((bubble, index) => {
      const normalizedTime = (bubble.totalTime - minTime) / timeRange;
      const radius = minRadius + (maxRadius - minRadius) * normalizedTime;

      // Golden angle spiral placement for initial positions
      const angle = index * 2.4;
      const distance = Math.sqrt(index + 1) * (minRadius * 2.5);

      const x = dimensions.width / 2 + Math.cos(angle) * distance;
      const y = dimensions.height / 2 + Math.sin(angle) * distance;

      return {
        ...bubble,
        radius,
        x: Math.max(radius + edgeMargin, Math.min(dimensions.width - radius - edgeMargin, x)),
        y: Math.max(radius + edgeMargin, Math.min(dimensions.height - radius - edgeMargin, y)),
        opacity: 0.85 + normalizedTime * 0.15
      };
    });

    // Pack bubbles so each new one touches at least one placed bubble and
    // does not overlap any other bubble.
    const center = { x: dimensions.width / 2, y: dimensions.height / 2 };
    const padding = window.innerWidth <= 480 ? 2 : 3;
    const sortedBubbles = bubbles.slice().sort((a, b) => b.radius - a.radius);
    const placed = [];

    sortedBubbles.forEach((bubble, index) => {
      if (index === 0) {
        bubble.x = center.x;
        bubble.y = center.y;
      } else {
        let best = null;
        let bestTouches = -1;
        let bestDistance = Infinity;

        for (const p of placed) {
          for (let step = 0; step < 36; step++) {
            const angle = (step * 2 * Math.PI) / 36;
            const cx =
              p.x +
              Math.cos(angle) * (p.radius + bubble.radius + padding);
            const cy =
              p.y +
              Math.sin(angle) * (p.radius + bubble.radius + padding);

            const clampedX = Math.max(
              bubble.radius + edgeMargin,
              Math.min(dimensions.width - bubble.radius - edgeMargin, cx)
            );
            const clampedY = Math.max(
              bubble.radius + edgeMargin,
              Math.min(dimensions.height - bubble.radius - edgeMargin, cy)
            );

            const overlaps = placed.some((pb) => {
              const pdx = clampedX - pb.x;
              const pdy = clampedY - pb.y;
              const d = Math.sqrt(pdx * pdx + pdy * pdy);
              return d < pb.radius + bubble.radius + padding;
            });

            if (overlaps) continue;

            // Count how many already-placed bubbles the candidate is tangent to
            const touches = placed.reduce((count, pb) => {
              const pdx = clampedX - pb.x;
              const pdy = clampedY - pb.y;
              const d = Math.sqrt(pdx * pdx + pdy * pdy);
              const target = pb.radius + bubble.radius + padding;
              return count + (Math.abs(d - target) <= 2 ? 1 : 0);
            }, 0);

            const distToCenter = Math.sqrt(
              (clampedX - center.x) ** 2 + (clampedY - center.y) ** 2
            );
            if (
              touches > bestTouches ||
              (touches === bestTouches && distToCenter < bestDistance)
            ) {
              bestTouches = touches;
              bestDistance = distToCenter;
              best = { x: clampedX, y: clampedY };
            }
          }
        }

        if (best) {
          bubble.x = best.x;
          bubble.y = best.y;
        } else {
          // Fallback: golden-angle spiral position if no perfect tangent slot fits
          const angle = index * 2.4;
          const distance = Math.sqrt(index + 1) * (minRadius * 2.5);
          bubble.x = Math.max(
            bubble.radius + edgeMargin,
            Math.min(dimensions.width - bubble.radius - edgeMargin, center.x + Math.cos(angle) * distance)
          );
          bubble.y = Math.max(
            bubble.radius + edgeMargin,
            Math.min(dimensions.height - bubble.radius - edgeMargin, center.y + Math.sin(angle) * distance)
          );
        }
      }

      placed.push(bubble);
    });

    // Center the placed bubble group within the chart area when the data
    // doesn't fill the whole SVG, so the page and fullscreen views look
    // balanced while the share PNG (cropped to content) remains unchanged.
    if (bubbles.length > 0) {
      const minX = Math.min(...bubbles.map((b) => b.x - b.radius));
      const maxX = Math.max(...bubbles.map((b) => b.x + b.radius));
      const minY = Math.min(...bubbles.map((b) => b.y - b.radius));
      const maxY = Math.max(...bubbles.map((b) => b.y + b.radius));
      const groupWidth = maxX - minX;
      const groupHeight = maxY - minY;
      const desiredMinX = edgeMargin + Math.max(0, (dimensions.width - 2 * edgeMargin - groupWidth) / 2);
      const desiredMinY = edgeMargin + Math.max(0, (dimensions.height - 2 * edgeMargin - groupHeight) / 2);
      const dx = desiredMinX - minX;
      const dy = desiredMinY - minY;
      bubbles.forEach((b) => {
        b.x = Math.max(b.radius + edgeMargin, Math.min(dimensions.width - b.radius - edgeMargin, b.x + dx));
        b.y = Math.max(b.radius + edgeMargin, Math.min(dimensions.height - b.radius - edgeMargin, b.y + dy));
      });
    }

    setBubbleData(bubbles);
    setLoading(false);
  }, [timelineData, chartRange, rangeMode, dimensions]);

  useEffect(() => {
    calculateBubbleData();
  }, [calculateBubbleData]);


  // Compute the bounding box of all placed bubbles (plus a glow margin)
  // so the exported PNG can be cropped to the actual chart content.
  const calculateCropBounds = useCallback(() => {
    if (!bubbleData.length) return null;
    const margin = 5;
    const minX = Math.min(...bubbleData.map((b) => b.x - b.radius)) - margin;
    const maxX = Math.max(...bubbleData.map((b) => b.x + b.radius)) + margin;
    const minY = Math.min(...bubbleData.map((b) => b.y - b.radius)) - margin;
    const maxY = Math.max(...bubbleData.map((b) => b.y + b.radius)) + margin;
    return {
      x: Math.max(0, minX),
      y: Math.max(0, minY),
      width: Math.min(dimensions.width, maxX) - Math.max(0, minX),
      height: Math.min(dimensions.height, maxY) - Math.max(0, minY),
    };
  }, [bubbleData, dimensions.width, dimensions.height]);

  // Share handler - only works if onShare prop is provided
  const handleShare = useCallback(async () => {
    if (!chartRef.current || !onShare) return;

    try {
      const svgElement = svgRef.current;
      if (!svgElement) return;

      const file = await svgElementToPngFile(svgElement, {
        width: dimensions.width,
        height: dimensions.height,
        cropBounds: calculateCropBounds(),
        filename: 'sessions-bubble-chart.png',
      });

      if (file) {
        onShare(file);
      }
    } catch (error) {
      logger.error('Error sharing chart:', error);
    }
  }, [onShare, dimensions.width, dimensions.height, calculateCropBounds]);

  // Allow the fullscreen modal header to trigger chart image sharing.
  // The trigger is consumed only once per increment so a parent re-render
  // cannot reopen the share sheet after it has been closed.
  const lastShareTriggerRef = useRef(0);
  useEffect(() => {
    if (shareTrigger > 0 && shareTrigger !== lastShareTriggerRef.current) {
      lastShareTriggerRef.current = shareTrigger;
      handleShare();
    }
  }, [shareTrigger, handleShare]);

  const { width, height } = dimensions;

  return (
    <div className="chart-card" style={{
      backgroundColor: "var(--bg-secondary, #fafbfc)",
      borderRadius: "12px",
      padding: isFullscreen ? 0 : "clamp(0.5rem, 1.5vw, 1rem)",
      position: "relative",
      overflow: "hidden",
      display: isFullscreen ? "flex" : undefined,
      flexDirection: isFullscreen ? "column" : undefined,
      height: isFullscreen ? "100%" : undefined
    }}>
      {/* Bubble Chart */}
      <div ref={chartRef} style={{ position: "relative", flex: isFullscreen ? 1 : undefined, height: isFullscreen ? "100%" : undefined }}>
        {/* Chart Action Buttons - top-right corner of the chart area (hidden in fullscreen because the modal provides its own) */}
        {(onShare || onExpand) && !isFullscreen && (
          <div style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            zIndex: 10
          }}>
            {onShare && shareStatus && (
              <span style={{
                fontSize: "clamp(0.75rem, 2vw, 0.875rem)",
                color: "var(--pista-green-primary, #2E7D32)",
                fontWeight: "600",
                backgroundColor: "var(--bg-primary, white)",
                padding: "0.25rem 0.5rem",
                borderRadius: "6px"
              }}>
                {shareStatus}
              </span>
            )}

            {onShare && (
              <button
                onClick={handleShare}
                style={{
                  padding: "clamp(0.4rem, 1.5vw, 0.55rem) clamp(0.5rem, 2vw, 0.75rem)",
                  borderRadius: "8px",
                  border: "1px solid var(--pista-green-secondary, #66BB6A)",
                  backgroundColor: "var(--pista-green-primary, #2E7D32)",
                  color: "var(--pista-button-text, white)",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "clamp(0.75rem, 2vw, 0.875rem)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s ease"
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "var(--pista-green-hover, #388E3C)";
                  e.target.style.color = "var(--pista-button-text, #FFFFFF)";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "var(--pista-green-primary, #2E7D32)";
                  e.target.style.color = "var(--pista-button-text, white)";
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ flexShrink: 0 }}
                >
                  <path
                    d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0076 5.24886 15.0224 5.36983L9.06253 8.36983C8.68487 7.56027 7.86668 7 6.92857 7C5.54811 7 4.42857 8.11954 4.42857 9.5C4.42857 10.8805 5.54811 12 6.92857 12C7.86668 12 8.68487 11.4397 9.06253 10.6302L15.0224 13.6302C15.0076 13.7511 15 13.8745 15 14C15 15.6569 16.3431 17 18 17C19.6569 17 21 15.6569 21 14C21 12.3431 19.6569 11 18 11C17.0619 11 16.2437 11.5603 15.866 12.3698L9.90616 9.36983C9.92097 9.24886 9.92857 9.12548 9.92857 9C9.92857 8.87452 9.92097 8.75114 9.90616 8.63017L15.866 5.63017C16.2437 6.43973 17.0619 7 18 7Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            )}

            {onExpand && (
              <button
                onClick={onExpand}
                title="View fullscreen"
                aria-label="View fullscreen"
                style={{
                  padding: "clamp(0.4rem, 1.5vw, 0.55rem)",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color, #ddd)",
                  backgroundColor: "var(--bg-primary, white)",
                  color: "var(--text-primary, #333)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Maximize2 size={16} />
              </button>
            )}
          </div>
        )}

        <svg ref={svgRef} width={width} height={height} style={{ display: "block" }}>
          {/* Background */}
          <rect width={width} height={height} fill="var(--bg-secondary, #fafbfc)" />

          {/* Bubbles */}
          {bubbleData.map((bubble) => (
            <g key={bubble.id}>
              {/* Bubble circle */}
              <circle
                cx={bubble.x}
                cy={bubble.y}
                r={bubble.radius}
                fill="var(--pista-green-primary, #2E7D32)"
                opacity={bubble.opacity}
                stroke={isUserWinnerForGame(bubble.id, user, timelineData) ? "#FFD700" : "var(--pista-green-secondary, #66BB6A)"}
                strokeWidth={isUserWinnerForGame(bubble.id, user, timelineData) ? "4" : "2"}
                style={{
                  cursor: "pointer",
                  transform: "none",
                  transition: "none",
                  filter: isUserWinnerForGame(bubble.id, user, timelineData) ? "drop-shadow(0 0 8px rgba(255, 215, 0, 0.6))" : "none"
                }}
                onMouseEnter={(_e) => {
                  setTooltip({
                    show: true,
                    x: bubble.x,
                    y: bubble.y - bubble.radius - 10,
                    game: bubble,
                    sessions: bubble.sessions,
                    totalTime: bubble.totalTime
                  });
                }}
                onMouseLeave={() => {
                  setTooltip({ show: false });
                }}
              />

              {/* Game image - clipped to the inner edge of the stroke so
                  the cover fills the circle with only the colored ring showing.
                  The clip-path id is namespaced so multiple chart instances (e.g.
                  the page and fullscreen modal) never share an id. */}
              <defs>
                <clipPath id={`${clipNs}-clip-${bubble.id}`}>
                  <circle cx={bubble.x} cy={bubble.y} r={bubble.radius - 2} />
                </clipPath>
              </defs>

              {bubble.id && bubble.image && (
                <image
                  href={bubble.image}
                  x={bubble.x - bubble.radius + 2}
                  y={bubble.y - bubble.radius + 2}
                  width={(bubble.radius - 2) * 2}
                  height={(bubble.radius - 2) * 2}
                  clipPath={`url(#${clipNs}-clip-${bubble.id})`}
                  preserveAspectRatio="xMidYMid slice"
                  style={{
                    pointerEvents: "none",
                    transform: "none",
                    transition: "none"
                  }}
                />
              )}

                          </g>
          ))}
        </svg>

        {/* Tooltip - positioned above bubble */}
        {tooltip.show && tooltip.game && (
          <div
            style={{
              position: "absolute",
              left: tooltip.x - 100,
              top: tooltip.y,
              transform: "translateX(-50%)",
              backgroundColor: "var(--text-primary, #333)",
              color: "white",
              padding: "8px 12px",
              borderRadius: "6px",
              fontSize: "clamp(0.75rem, 2vw, 0.875rem)",
              pointerEvents: "none",
              zIndex: 1000,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
              maxWidth: "200px",
              textAlign: "center",
              transition: "none"
            }}
          >
            <div style={{ fontWeight: "600", marginBottom: "4px" }}>
              {tooltip.game.gameName}
            </div>
            <div style={{ fontSize: "0.8rem", opacity: 0.9 }}>
              {tooltip.sessions} sessions • {Math.round(tooltip.totalTime)}min
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)"
          }}>
            <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>⏳</div>
            <div style={{ color: "var(--text-secondary, #666)", fontSize: "1rem" }}>
              Loading game details...
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && bubbleData.length === 0 && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "var(--bg-secondary, #fafbfc)"
          }}>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🎮</div>
            <div style={{ textAlign: "center", color: "var(--text-secondary, #666)" }}>
              <div style={{ fontSize: "clamp(0.875rem, 2vw, 1rem)" }}>
                No games played in this timeframe
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chart Stats Footer - sessions, hours, games */}
      {bubbleData.length > 0 && (
        <div style={{
          marginTop: "1rem",
          display: "flex",
          justifyContent: "center",
          gap: "2rem",
          flexWrap: "wrap"
        }}>
          <div style={{ textAlign: "center" }}>
            <div style={{
              fontSize: "clamp(1.25rem, 3vw, 1.5rem)",
              fontWeight: "700",
              color: "var(--pista-green-primary, #2E7D32)"
            }}>
              {bubbleData.length}
            </div>
            <div style={{
              fontSize: "clamp(0.75rem, 2vw, 0.875rem)",
              color: "var(--text-secondary, #666)"
            }}>
              Games
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{
              fontSize: "clamp(1.25rem, 3vw, 1.5rem)",
              fontWeight: "700",
              color: "var(--pista-green-primary, #2E7D32)"
            }}>
              {bubbleData.reduce((sum, b) => sum + b.sessions, 0)}
            </div>
            <div style={{
              fontSize: "clamp(0.75rem, 2vw, 0.875rem)",
              color: "var(--text-secondary, #666)"
            }}>
              Sessions
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{
              fontSize: "clamp(1.25rem, 3vw, 1.5rem)",
              fontWeight: "700",
              color: "var(--pista-green-primary, #2E7D32)"
            }}>
              {(bubbleData.reduce((sum, b) => sum + b.totalTime, 0) / 60).toFixed(1)}
            </div>
            <div style={{
              fontSize: "clamp(0.75rem, 2vw, 0.875rem)",
              color: "var(--text-secondary, #666)"
            }}>
              Hours
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BubbleChart;
